import React, { useState, useEffect } from 'react';
import i18next from 'i18next';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import './TrialNotifications.scss';
import { DataTrackerEvents } from '../../dataTracker/DataTrackerEvents';
import { sendDataTrackerEventHandler } from '../../dataTracker/DataTrackerHelper';
import { Trans } from 'react-i18next';
import { User } from '../../../models/User';
import { selectUserInfo } from '../../../main/store/User/reducer';
import { AcceptedExtraData } from '@signaturit/data-tracker-client';

export enum NotificationType {
    danger = 'danger',
    warning = 'warning',
    info = 'info',
}

export enum RemainingDays {
    sevenDaysLeft = 7,
    fourDaysLeft = 4,
    threeDaysLeft = 3,
    oneDayLeft = 1,
}

export interface Notifications {
    info: {
        translation: string;
        value: string;
        translationLink: string;
    };
    warning: {
        translation: string;
        value: string;
        translationLink: string;
        id: string;
    };
    danger: {
        translation: string;
        value: string;
        translationLink: string;
        id: string;
    };
}

const notificationObj: Readonly<Notifications> = {
    info: {
        translation: 'default._notifications.your_account_expire_from_7_to_4_days',
        value: NotificationType.info,
        translationLink: 'default._notifications.get_your_plan_today',
    },
    warning: {
        translation: 'default._notifications.your_account_expire_from_3_to_1_days',
        value: NotificationType.warning,
        translationLink: 'default._notifications.buy_now',
        id: 'trial-button-warning',
    },
    danger: {
        translation: 'default._notifications.your_account_is_expired',
        value: NotificationType.danger,
        translationLink: 'default._notifications.buy_now_camelcase',
        id: 'trial-button-danger',
    },
};

const setNotification = (remainingDays: number, notification: any, setState: any, customStyle: boolean) => {
    useEffect(() => {
        setState(notification.value);
    });

    const sendDataTracker = () => {
        const trackerData = {
            remainingDays,
        };
        sendDataTrackerEventHandler(DataTrackerEvents.clickTrialBarButton, trackerData, [AcceptedExtraData.limits]);
    };

    return (
        <div className={`trial-notifications__text${customStyle ? ' customTextColor' : ''}`}>
            <p>
                <Trans i18nKey={notification.translation} values={{ remaining_days: remainingDays }} />

                <a
                    type="button"
                    href="#/settings/upgrade"
                    onClick={sendDataTracker}
                    id={notification.id ? `trial-button-${notification.value}` : 'trial-button-notice'}
                >
                    {i18next.t(notification.translationLink)}
                </a>
            </p>
        </div>
    );
};

const RenderNotification = (remainingDays: number, setNotificationType: any) => {
    if (remainingDays <= RemainingDays.sevenDaysLeft && remainingDays >= RemainingDays.fourDaysLeft) {
        const { info } = notificationObj;
        return setNotification(remainingDays, info, setNotificationType, false);
    } else if (remainingDays <= RemainingDays.threeDaysLeft && remainingDays >= RemainingDays.oneDayLeft) {
        const { warning } = notificationObj;
        return setNotification(remainingDays, warning, setNotificationType, true);
    } else {
        const { danger } = notificationObj;
        return setNotification(remainingDays, danger, setNotificationType, true);
    }
};

export const TrialNotifications = () => {
    const [notificationType, setNotificationType] = useState<string>('');

    const { sevenDaysLeft } = RemainingDays;
    const user: User | undefined = useSelector(selectUserInfo);
    const expireDate = dayjs(user?.notifications.trialExpiresAt);
    const remainingDays =
        expireDate.diff(dayjs(), 'day') > sevenDaysLeft ? sevenDaysLeft : expireDate.diff(dayjs(), 'day');

    return (
        <div className="trial-notifications">
            <div className={`trial-notifications__inner ${notificationType}-notification`}>
                {RenderNotification(remainingDays, setNotificationType)}
            </div>
        </div>
    );
};
